import { NavBar } from './nav-bar';
import { meetingUrl } from './urls';
import { useEffect } from 'preact/hooks';
import { MeetingDetail } from './meeting-detail';
import { NewMeetingForm } from './new-meeting-form';
import { NewMeetingSeriesForm } from './new-meeting-series-form';
import { Case } from '@components/conditional';
import { useState } from 'preact/hooks';
import { groupMeetingsByStatus } from 'shared/meeting-utils';
import { EmptyPage } from '@components/empty-page';
import { IcoPresentation } from '@components/icons';
import { GuideCoursePage } from '@course/components/guide-course-page';
import { URLS } from 'shared/urls';
import { completeCourseStep } from '@course/components/course-checklist';
import { useCurrentTenant } from '@components/router/session-context';
import { load, addMeeting, deleteMeeting, updateMeeting } from './state';
import { seriesUrl } from './urls';
import { LoadedProps } from '@components/router';
import { defCoursesRoute } from '@course/components/courses-app-router';

export const route = defCoursesRoute({
  Page,
  load,
  authLevel: 'guide',
  // The page state is getting reset when navigating between tabs of the meeting detail page.
  // So we need to set the cache key to avoid this.
  key: ({ params }) => `meetings_${params.courseId}`,
});

function Page(props: LoadedProps<typeof load>) {
  const { state, setState } = props;
  const { course, meetings, series } = state;
  const { meetingId } = props.params;
  const { terminology } = useCurrentTenant();
  const isCreatePage = meetingId === 'new';
  const selectedMeeting = meetings.find((w) => w.id === meetingId);
  const { draft, past, upcoming } = groupMeetingsByStatus(meetings);

  // Go to the first available meeting, if none was requested.
  useEffect(() => {
    if (!isCreatePage && !selectedMeeting && meetings.length > 0) {
      const firstId = upcoming[0]?.id ?? past[0]?.id;
      props.router.rewrite(meetingUrl({ courseId: course.id, meetingId: firstId }));
    }
  }, [isCreatePage, selectedMeeting, course]);

  return (
    <GuideCoursePage
      course={course}
      isLoading={!course}
      viewLink={
        selectedMeeting &&
        URLS.student.meeting({
          course,
          meeting: selectedMeeting,
        })
      }
      documentTitle={selectedMeeting?.title}
      type="meetings"
    >
      <Case when={meetings.length > 0 && !isCreatePage}>
        <NavBar draft={draft} upcoming={upcoming} past={past} series={series} />
      </Case>
      <div class="flex flex-col grow p-4 md:p-8 max-w-5xl mx-auto">
        <Case
          when={!isCreatePage}
          fallback={
            <NewMeetingChooser
              course={course}
              onCreateMeeting={(meeting) => {
                addMeeting(setState, meeting);
                props.router.goto(meetingUrl({ courseId: course.id, meetingId: meeting.id }));
                completeCourseStep(props.router, 'createMeeting');
              }}
              onCreateSeries={(result) => {
                props.router.goto(seriesUrl({ courseId: course.id, seriesId: result.series.id }));
                completeCourseStep(props.router, 'createMeeting');
              }}
            />
          }
        >
          <Case when={meetings.length === 0}>
            <EmptyPage
              Ico={IcoPresentation}
              actionText={`Create your first ${terminology.meeting}`}
              description={`You haven't created any ${terminology.meetings}.`}
              title={`Create a ${terminology.meeting}`}
              centeredDescription
              href={meetingUrl({ courseId: course.id, page: 'new' })}
            />
          </Case>
          {selectedMeeting && (
            <MeetingDetail
              course={course}
              key={selectedMeeting.id}
              meeting={selectedMeeting}
              hasEnrolledStudents={course.numStudents ? course.numStudents > 0 : false}
              onAdd={(meeting) => addMeeting(setState, meeting)}
              onDelete={() => deleteMeeting(setState, selectedMeeting.id)}
              onUpdate={(meeting) => updateMeeting(setState, selectedMeeting.id, meeting)}
            />
          )}
        </Case>
      </div>
    </GuideCoursePage>
  );
}

type Mode = 'oneoff' | 'recurring';

function NewMeetingChooser({
  course,
  onCreateMeeting,
  onCreateSeries,
}: {
  course: Parameters<typeof NewMeetingForm>[0]['course'] & { calendarInvitesEnabled?: boolean };
  onCreateMeeting: Parameters<typeof NewMeetingForm>[0]['onCreate'];
  onCreateSeries: Parameters<typeof NewMeetingSeriesForm>[0]['onCreate'];
}) {
  const seriesEnabled = !!course.calendarInvitesEnabled;
  const [mode, setMode] = useState<Mode>('oneoff');
  if (!seriesEnabled) {
    return <NewMeetingForm course={course} onCreate={onCreateMeeting} />;
  }
  return (
    <div class="flex flex-col w-full max-w-5xl mx-auto pt-8">
      <div class="inline-flex self-start rounded-lg bg-gray-100 dark:bg-gray-800 p-1 mb-2">
        <button
          type="button"
          class={`px-4 py-2 text-sm rounded-md ${mode === 'oneoff' ? 'bg-white dark:bg-gray-900 shadow font-medium' : 'text-gray-600 dark:text-gray-300'}`}
          onClick={() => setMode('oneoff')}
        >
          One-off
        </button>
        <button
          type="button"
          class={`px-4 py-2 text-sm rounded-md ${mode === 'recurring' ? 'bg-white dark:bg-gray-900 shadow font-medium' : 'text-gray-600 dark:text-gray-300'}`}
          onClick={() => setMode('recurring')}
        >
          Recurring series
        </button>
      </div>
      {mode === 'oneoff' ? (
        <NewMeetingForm course={course} onCreate={onCreateMeeting} />
      ) : (
        <NewMeetingSeriesForm course={course} onCreate={onCreateSeries} />
      )}
    </div>
  );
}
